import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withDebugTracing,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { provideEffects } from '@ngrx/effects';
import { RouterStateSerializer, provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { provideMarkdown } from 'ngx-markdown';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { provideToastr } from 'ngx-toastr';

import { initializeAuth } from '@f4rm/app-core/services/auth/auth-initializer';
import { APP_ENVIRONMENT } from '@f4rm/app-core/services/config/app-environment.token';
import { GlobalErrorHandler } from '@f4rm/app-core/services/error/global-error-handler';
import { I18nService } from '@f4rm/app-core/services/i18n/i18n.service';
import { HttpTranslateLoader } from '@f4rm/app-core/services/initialize/store-translation-loader';
import { RouterObserverService } from '@f4rm/app-core/services/router/router-observer.service';
import { AccountingStore } from '@f4rm/app-core/store/accounting.store';
import { ActivityLogStore } from '@f4rm/app-core/store/activity-log.store';
import { AuthStore } from '@f4rm/app-core/store/auth.store';
import { BuildingStore } from '@f4rm/app-core/store/building.store';
import { ContractStore } from '@f4rm/app-core/store/contract.store';
import { CropStore } from '@f4rm/app-core/store/crop.store';
import { EquipmentStore } from '@f4rm/app-core/store/equipment.store';
import { FarmMembersStore } from '@f4rm/app-core/store/farm-members.store';
import { FarmStore } from '@f4rm/app-core/store/farm.store';
import { FieldStore } from '@f4rm/app-core/store/field.store';
import { InsuranceClaimStore } from '@f4rm/app-core/store/insurance-claim.store';
import { InsurancePolicyStore } from '@f4rm/app-core/store/insurance-policy.store';
import { LayoutStore } from '@f4rm/app-core/store/layout.store';
import { LivestockStore } from '@f4rm/app-core/store/livestock.store';

import { MenuStore } from '@f4rm/app-core/store/menu.store';
import { coreReducers } from '@f4rm/app-core/store/ngrx/core-reducers';
import { RouterEffects } from '@f4rm/app-core/store/ngrx/effects/router.effect';
import { TaskLogStore } from '@f4rm/app-core/store/task-log.store';
import { TaskStore } from '@f4rm/app-core/store/task.store';
import { UserStore } from '@f4rm/app-core/store/user.store';
import { CustomRouterStateSerializer } from '@f4rm/app-core/store/utils/custom-router-state-serializer';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { provideKeycloakAngular } from './keycloak.provider';
import { AppCustomPreloader } from './preloader';

export const appConfig: ApplicationConfig = {
  providers: [
    // Router observer for 404 error logging
    RouterObserverService,

    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([includeBearerTokenInterceptor])
    ),
    provideNoopAnimations(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding(),
      withPreloading(AppCustomPreloader),
      withDebugTracing(),
    ),
    provideZonelessChangeDetection(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),

    // NGRX - Main store must be initialized first, then feature stores
    provideStore(coreReducers),
    provideRouterStore({
      serializer: CustomRouterStateSerializer,
      stateKey: 'router',
    }),
    provideEffects([RouterEffects]),
    provideStoreDevtools({ logOnly: !isDevMode() }),

    // SIGNAL STORES
    AccountingStore,
    ActivityLogStore,
    AuthStore,
    BuildingStore,
    ContractStore,
    CropStore,
    EquipmentStore,
    FarmStore,
    FarmMembersStore,
    FieldStore,
    InsuranceClaimStore,
    InsurancePolicyStore,
    LayoutStore,
    LivestockStore,

    MenuStore,
    TaskLogStore,
    TaskStore,
    UserStore,

    // FIREBASE (for non-auth services only)
    provideFirebaseApp(() => initializeApp(environment.config.firebase)),

    // KEYCLOAK
    provideKeycloakAngular(environment),

    // THIRD PARTIES
    provideTranslateService({
      fallbackLang: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: HttpTranslateLoader,
      },
    }),
    provideToastr({
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),
    provideMarkdown(),

    // AUTH INITIALIZATION - Must complete before routing
    provideAppInitializer(initializeAuth()),

    // LANGUAGE SERVICE INITIALIZATION
    provideAppInitializer(() => {
      const i18nService = inject(I18nService);
      return i18nService.init();
    }),

    // FARM CONTEXT RESTORATION
    provideAppInitializer(() => {
      const farmStore = inject(FarmStore);
      farmStore.restoreCurrentFarm();
    }),

    importProvidersFrom(
      FormsModule,
      ReactiveFormsModule,
      NgScrollbarModule
    ),
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => navigator.language,
    },

    { provide: APP_ENVIRONMENT, useValue: environment },
    {
      provide: RouterStateSerializer,
      useClass: CustomRouterStateSerializer,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
  ],
};
